import React, { useEffect, useState } from 'react';
import { Table, Tag, Space, Button, Input, Select, Modal, Image, Tooltip, Popconfirm, Card, Row, Col, Typography } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Article, ArticleFilter } from '../types/articles';
import articleService from '../services/articleService';
import { PORTAL_URL } from '../config';
import { useSnackbar } from 'notistack';

const { Search } = Input;
const { Title, Text } = Typography;

const Articles: React.FC = () => {
    const navigate = useNavigate();
    const [articles, setArticles] = useState<Article[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filter, setFilter] = useState<ArticleFilter>({
        page: 1,
        limit: 10
    });
    const [search, setSearch] = useState<string>('');
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const fetchArticles = async () => {
        try {
            setLoading(true);
            const result = await articleService.getArticles(filter);
            setArticles(result.articles);
            setTotal(result.total);
        } catch (error) {
            console.error('Error fetching articles:', error);
            enqueueSnackbar('Không thể tải danh sách bài viết', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArticles();
    }, [filter]);

    const columns = [
        {
            title: 'Hình ảnh',
            key: 'featuredImage',
            align: 'center' as const,
            width: 80,
            render: (record: Article) => (
                <Tooltip title="Hình ảnh đại diện">
                    {record.featuredImage && record.featuredImage.url ? (
                        <Image
                            width={60}
                            height={40}
                            src={record.featuredImage.url}
                            alt={record.featuredImage.alt || 'No alt'}
                            style={{ objectFit: 'cover' }}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                        />
                    ) : (
                        <div className="flex justify-center items-center w-12 h-12 text-gray-400 bg-gray-200 rounded">
                            <span className="text-xs">No img</span>
                        </div>
                    )}
                </Tooltip>
            ),
        },
        {
            title: 'Tiêu đề',
            dataIndex: 'title',
            key: 'title',
            align: 'left' as const,
            ellipsis: true,
            render: (text: string, record: Article) => (
                <Tooltip title={`${text} - ${record.description.substring(0, 500)}...`}>
                    <div>
                        <div className="font-medium">{text}</div>
                        <div className="text-sm text-gray-500">{record.description.substring(0, 100)}...</div>
                    </div>
                </Tooltip>
            ),
        },
        // {
        //     title: 'Tác giả',
        //     dataIndex: ['author', 'email'],
        //     key: 'author',
        //     align: 'center' as const,
        //     ellipsis: true,
        //     width: 100,
        //     render: (email: string) => (
        //         <Tooltip title={`Email: ${email}`}>
        //             <span>{email}</span>
        //         </Tooltip>
        //     ),
        // },
        {
            title: 'Loại',
            dataIndex: 'type',
            key: 'type',
            align: 'center' as const,
            ellipsis: true,
            width: 100,
            render: (type: string) => {
                let color = 'default';
                let text = '';
                switch (type) {
                    case 'blog':
                        color = 'blue';
                        text = 'Tin tức sự kiện nổi bật';
                        break;
                    case 'news':
                        color = 'green';
                        text = 'Hiểu về Lụa';
                        break;
                    case 'article':
                        color = 'orange';
                        text = 'Thời trang lụa';
                        break;
                    case 'heritage':
                        color = 'purple';
                        text = 'Lụa & Di sản';
                        break;
                    default:
                        text = type;
                }
                return (
                    <Tooltip title={`Loại bài viết: ${text}`}>
                        <Tag color={color}>{text}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as const,
            ellipsis: true,
            width: 140,
            render: (status: string) => {
                let color = 'default';
                let text = '';
                switch (status) {
                    case 'published':
                        color = 'green';
                        text = 'Đã xuất bản';
                        break;
                    case 'draft':
                        color = 'orange';
                        text = 'Bản nháp';
                        break;
                    case 'archived':
                        color = 'red';
                        text = 'Đã lưu trữ';
                        break;
                    default:
                        text = status;
                }
                return (
                    <Tooltip title={`Trạng thái: ${text}`}>
                        <Tag color={color}>{text}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center' as const,
            width: 120,
            render: (date: string) => (
                <Tooltip title={`Ngày tạo: ${new Date(date).toLocaleString('vi-VN')}`}>
                    <span>{new Date(date).toLocaleDateString('vi-VN')}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Thao tác',
            key: 'action',
            align: 'center' as const,
            width: 150,
            render: (_: any, record: Article) => (
                <Space>
                    <Tooltip title="Xem chi tiết">
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => window.open(`${PORTAL_URL}/news/${record.slug}`, '_blank')}
                            type="text"
                        />
                    </Tooltip>
                    <Tooltip title="Chỉnh sửa">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/admin/articles/${record._id}`)}
                            type="text"
                        />
                    </Tooltip>
                    <Tooltip title="Xóa">
                        <Popconfirm
                            title="Xóa bài viết"
                            description="Bạn có chắc chắn muốn xóa bài viết này?"
                            onConfirm={() => handleDelete(record._id)}
                            okText="Xóa"
                            cancelText="Hủy"
                        >
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                type="text"
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const handleTableChange = (pagination: any) => {
        setFilter(prev => ({
            ...prev,
            page: pagination.current,
            limit: pagination.pageSize
        }));
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const handleSearch = (value: string) => {
        setFilter(prev => ({
            ...prev,
            search: value,
            page: 1
        }));
    };

    const handleDelete = async (id: string) => {
        setSelectedArticleId(id);
        setDeleteModalVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedArticleId) return;

        try {
            setDeleteLoading(true);
            await articleService.deleteArticle(selectedArticleId);
            enqueueSnackbar('Xóa bài viết thành công', { variant: 'success' });
            setDeleteModalVisible(false);
            fetchArticles(); // Refresh lại danh sách
        } catch (error) {
            enqueueSnackbar('Xóa bài viết thất bại', { variant: 'error' });
        } finally {
            setDeleteLoading(false);
            setSelectedArticleId(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalVisible(false);
        setSelectedArticleId(null);
    };

    return (
        <div className="p-4">
            {/* Header row với bộ lọc */}
            <Card className="mb-4">
                <Title level={2} className="m-0 mb-4">
                    Quản lý bài viết
                </Title>
                <Row justify="space-between" align="middle" gutter={16}>
                    <Col>
                        <Space>
                            <Input
                                placeholder="Tìm kiếm bài viết..."
                                value={search || ''}
                                onChange={(e) => setSearch(e.target.value)}
                                onPressEnter={(e) => handleSearch((e.target as HTMLInputElement).value)}
                                prefix={<SearchOutlined />}
                                allowClear
                                style={{ width: 300 }}
                            />
                            <Select
                                placeholder="Loại bài viết"
                                style={{ width: 150 }}
                                allowClear
                                onChange={(value) => setFilter(prev => ({ ...prev, type: value }))}
                            >
                                <Select.Option value="blog">Tin tức sự kiện nổi bật</Select.Option>
                                <Select.Option value="news">Hiểu về Lụa</Select.Option>
                                <Select.Option value="article">Thời trang lụa</Select.Option>
                                <Select.Option value="heritage">Lụa & Di sản</Select.Option>
                            </Select>
                            <Select
                                placeholder="Trạng thái"
                                style={{ width: 150 }}
                                allowClear
                                onChange={(value) => setFilter(prev => ({ ...prev, status: value }))}
                            >
                                <Select.Option value="published">Đã xuất bản</Select.Option>
                                <Select.Option value="draft">Bản nháp</Select.Option>
                                <Select.Option value="archived">Đã lưu trữ</Select.Option>
                            </Select>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchArticles}
                                loading={loading}
                                shape="circle"
                                title="Làm mới"
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => navigate('/admin/articles/new')}
                            >
                                Thêm bài viết
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Bảng bài viết */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={articles}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        showTotal: (total) => `Tổng số ${total} bài viết`
                    }}
                    onChange={handleTableChange}
                    bordered
                />
            </Card>

            <Modal
                title="Xác nhận xóa"
                open={deleteModalVisible}
                onOk={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                okText="Xóa"
                cancelText="Hủy"
                okButtonProps={{ danger: true, loading: deleteLoading }}
            >
                <p>Bạn có chắc chắn muốn xóa bài viết này?</p>
            </Modal>
        </div>
    );
};

export default Articles;
